import API_BASE from '../config';
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import LinkedInImport from '../components/LinkedInImport';
import ProfessionalTimeline from '../components/ProfessionalTimeline';

const API_URL = API_BASE;

// Official brand logo asset (frontend/public/images/brand/) — same file
// used verbatim on every other screen (landing, analyze, loading). Only
// `height` is set; width stays `auto` so the image's own aspect ratio is
// preserved and never distorted.
const Logo = ({ height = 26 }) => (
  <img
    src="/images/brand/successlane-logo-mark.png"
    alt="Successlane"
    style={{ height, width: 'auto', display: 'block' }}
  />
);

const IntakeFormPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Separate from `error` on purpose: `error` replaces the whole page (invalid/
  // expired link). This one is shown inline on the review step so a failed
  // file upload doesn't wipe the customer's already-filled-in answers.
  const [submitError, setSubmitError] = useState('');
  const [orderInfo, setOrderInfo] = useState(null);
  const [errors, setErrors] = useState({});
  const [uploadedFile, setUploadedFile] = useState(null);
  const fileInputRef = useRef(null);
  const [cvFile, setCvFile] = useState(null);
  const cvFileInputRef = useRef(null);
  const [linkedinInputMethod, setLinkedinInputMethod] = useState('import'); // 'import' | 'pdf' | 'cv'
  const [importedProfile, setImportedProfile] = useState(null);
  // LinkedIn URL from the free-evaluation session (sessionStorage), used as fallback
  // when the user chooses PDF/CV in the intake form instead of re-importing the URL.
  const [evaluatorLinkedinUrl, setEvaluatorLinkedinUrl] = useState(null);

  const [formData, setFormData] = useState({
    target_title: '',
    seniority: '',
    current_industry: '',
    target_industry: '',
    years_experience: '',
    dream_companies: ['', '', '', '', ''],
    job_description: '',
    linkedin_url: '',
    achievement_1: '',
    achievement_2: '',
    achievement_3: '',
    skills: '',
    extra_notes: ''
  });

  // Section 11: Multi-JD state (up to 3 for same target role)
  const [targetJds, setTargetJds] = useState([
    { text: '', source_type: 'pasted', source_url: '', matches_role: true }
  ]);

  const seniorityLevels = [
    'Junior (0–3 years)', 'Mid-level (3–6 years)', 'Senior (6–10 years)',
    'Lead / Manager', 'Director', 'VP', 'C-Suite'
  ];

  useEffect(() => {
    validateAccess();
    // Recover the LinkedIn URL captured during the free evaluation, then
    // pull the previously-scraped profile from our DB cache. This avoids a
    // second LinkdAPI call and shows the user the data we already have on file.
    try {
      const params = JSON.parse(sessionStorage.getItem('evaluationParams') || '{}');
      const url = params.linkedin_url;
      if (url) {
        setEvaluatorLinkedinUrl(url);
        (async () => {
          try {
            const r = await fetch(`${API_URL}/api/linkedin/cached?url=${encodeURIComponent(url)}`);
            if (r.ok) {
              const json = await r.json();
              const p = json.profile;
              if (p) {
                setImportedProfile({ ...p, profileId: p.profileId });
                if (!formData.skills && Array.isArray(p.skills) && p.skills.length) {
                  setFormData(prev => ({ ...prev, skills: p.skills.slice(0, 10).join(', ') }));
                }
              }
            }
          } catch (_) { /* fall back to LinkedInImport UI */ }
        })();
      }
    } catch (_) {}
  }, []);

  const validateAccess = async () => {
    const order = searchParams.get('order');
    const token = searchParams.get('token');

    if (!order || !token) {
      setError('Invalid link. Missing required parameters.');
      setLoading(false);
      return;
    }

    try {
      // No email in URL — token is the capability. Backend returns the
      // customer email in the validate response so we can pre-fill it.
      const response = await fetch(`${API_URL}/api/intake-form/validate?order=${encodeURIComponent(order)}&token=${encodeURIComponent(token)}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Invalid link');
      }

      if (data.already_completed) {
        // already=1 tells the success page this is a revisit (opened the
        // link again after already submitting, most likely from the same
        // email link in a different tab), not a fresh submission — it
        // shows calmer, no-confetti copy instead of "We're on it!".
        navigate(`/intake-success?order=${order}&name=${data.customer_name.split(' ')[0]}&already=1`, { replace: true });
        return;
      }

      setOrderInfo(data);
    } catch (err) {
      setError(err.message || 'This link is invalid or has expired.');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.target_title.trim()) newErrors.target_title = 'Please enter your target job title.';
    if (!formData.seniority) newErrors.seniority = 'Please select your seniority level.';
    if (!importedProfile && !uploadedFile) {
      newErrors.linkedin = 'Please import your LinkedIn profile via URL or upload a LinkedIn PDF.';
    }
    if (!cvFile) {
      newErrors.cv = 'Please upload your CV or resume.';
    }
    if (!formData.achievement_1.trim()) newErrors.achievement_1 = 'Please share at least one achievement.';
    // Section 11: Require at least one JD with text content
    const hasJd = targetJds.some(jd => jd.text && jd.text.trim());
    if (!hasJd) newErrors.job_description = 'Please paste at least one job description — it\'s the most important input for keyword matching.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProfileImported = (profileData, profileId) => {
    setImportedProfile({ ...profileData, profileId });
    setErrors(prev => ({ ...prev, linkedin: null }));
    // Auto-fill skills from imported profile if empty
    if (profileData && !formData.skills) {
      const skillsStr = (profileData.skills || []).slice(0, 10).join(', ');
      if (skillsStr) {
        setFormData(prev => ({ ...prev, skills: skillsStr }));
      }
    }
    // Scroll to achievements section
    setTimeout(() => {
      document.getElementById('achievements-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
  };

  const handleNext = () => {
    if (validateForm()) {
      setStep(2);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');

    try {
      const orderId = searchParams.get('order');

      // Upload LinkedIn PDF if provided. This used to be fire-and-forget
      // (errors only console.warn'd) and the form would submit anyway,
      // silently completing the order with no LinkedIn source material at
      // all — the customer saw "submitted successfully" while the file
      // never actually made it to storage, and the order got stuck later
      // with "Parse: failed" and no way for the admin to tell why. Now a
      // failed upload stops the submission here so the customer can retry
      // instead of losing the file without knowing it.
      if (uploadedFile && orderId) {
        const pdfForm = new FormData();
        pdfForm.append('file', uploadedFile);
        pdfForm.append('order_id', orderId);
        pdfForm.append('customer_email', orderInfo.customer_email);
        try {
          const pdfRes = await fetch(`${API_URL}/api/intake-form/upload-pdf`, {
            method: 'POST',
            body: pdfForm
          });
          if (!pdfRes.ok) {
            const detail = await pdfRes.json().catch(() => ({}));
            throw new Error(detail.detail || 'Failed to upload your LinkedIn PDF.');
          }
        } catch (pdfErr) {
          setSubmitError(pdfErr.message || 'Your LinkedIn PDF failed to upload. Please check your connection and try again.');
          setSubmitting(false);
          return;
        }
      }

      // Upload CV/resume if provided — same reasoning as the LinkedIn PDF
      // upload above: a swallowed failure here left orders with "No resume
      // uploaded" despite the customer having attached one and clicked
      // Submit successfully.
      if (cvFile && orderId) {
        const cvForm = new FormData();
        cvForm.append('file', cvFile);
        cvForm.append('order_id', orderId);
        cvForm.append('customer_email', orderInfo.customer_email);
        try {
          const cvRes = await fetch(`${API_URL}/api/intake-form/upload-cv`, {
            method: 'POST',
            body: cvForm
          });
          if (!cvRes.ok) {
            const detail = await cvRes.json().catch(() => ({}));
            throw new Error(detail.detail || 'Failed to upload your CV/resume.');
          }
        } catch (cvErr) {
          setSubmitError(cvErr.message || 'Your CV/resume failed to upload. Please check your connection and try again.');
          setSubmitting(false);
          return;
        }
      }

      const submitData = {
        // Auth: send order_id + token so the backend doesn't need email as a key.
        order_id: searchParams.get('order') || undefined,
        token:    searchParams.get('token') || undefined,
        customer_name: orderInfo.customer_name,
        customer_email: orderInfo.customer_email,
        customer_phone: '',
        customer_country: 'India',
        customer_state: '',
        target_title: formData.target_title,
        seniority: formData.seniority,
        years_experience: formData.years_experience,
        current_industry: formData.current_industry,
        target_industry: formData.target_industry,
        dream_companies: formData.dream_companies.filter(c => c.trim()),
        job_description: targetJds[0]?.text || formData.job_description, // backward compat: keep first JD in legacy column
        target_jds: targetJds.filter(jd => jd.text && jd.text.trim()),  // Section 11: new multi-JD field
        // Use the imported URL first; fall back to the URL captured during evaluation
        linkedin_url: importedProfile?.linkedin_url || evaluatorLinkedinUrl || null,
        achievement_1: formData.achievement_1,
        achievement_2: formData.achievement_2,
        achievement_3: formData.achievement_3,
        skills: formData.skills,
        extra_notes: formData.extra_notes,
        // Include imported profile data if available — keys match the API response shape
        imported_profile: importedProfile ? {
          profile_id: importedProfile.profileId,
          full_name: importedProfile.full_name,
          headline: importedProfile.headline,
          current_position: importedProfile.current_position,
          current_company: importedProfile.current_company,
          location: importedProfile.location,
          about: importedProfile.about,             // was about_section — API returns 'about'
          experiences: importedProfile.experiences, // was experience  — API returns 'experiences'
          education: importedProfile.education,
          skills: importedProfile.skills,
          linkedin_url: importedProfile.linkedin_url
        } : null
      };

      const response = await fetch(`${API_URL}/api/intake-form/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });

      const data = await response.json();

      // Race-condition backstop: this exact order was already submitted —
      // most likely via a second tab/device open on the same email link.
      // The backend guards against this even though the page-load check
      // already redirects a *revisit*, since that check can't see a
      // submission that completed elsewhere after this tab was loaded.
      // Same calm, no-confetti success screen as the revisit case, not a
      // jarring native alert().
      if (response.status === 409) {
        navigate(`/intake-success?order=${searchParams.get('order')}&name=${orderInfo.customer_name.split(' ')[0]}&already=1`, { replace: true });
        return;
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Submission failed');
      }

      navigate(`/intake-success?order=${data.order_id}&name=${orderInfo.customer_name.split(' ')[0]}`, { replace: true });
    } catch (err) {
      setSubmitError(err.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setUploadedFile(file);
      setErrors(prev => ({ ...prev, linkedin: null }));
    }
  };

  const handleCvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Validate by extension only — file.type is unreliable across browsers (especially Edge for .docx)
    if (!file.name.match(/\.(pdf|docx)$/i)) {
      setErrors(prev => ({ ...prev, cv: 'Please upload a PDF or Word (.docx) file.' }));
      e.target.value = '';
      return;
    }
    setCvFile(file);
    setErrors(prev => ({ ...prev, cv: null }));
  };

  const updateCompany = (index, value) => {
    const companies = [...formData.dream_companies];
    companies[index] = value;
    setFormData({ ...formData, dream_companies: companies });
  };

  if (loading) {
    return (
      <div className="intake-loading" style={{ fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, sans-serif" }}>
        <style>{`@keyframes sl-intake-spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ cursor: 'pointer', display: 'inline-flex' }} onClick={() => navigate('/')}><Logo height={30} /></div>
        <div style={{ width: 32, height: 32, border: '3px solid #efedf6', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'sl-intake-spin 0.8s linear infinite', margin: '20px 0 16px' }} />
        <p>Validating your link...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="intake-error" style={{ fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, sans-serif" }}>
        <div style={{ cursor: 'pointer', display: 'inline-flex' }} onClick={() => navigate('/')}><Logo height={30} /></div>
        <div className="error-icon" style={{ marginTop: 20 }}>🔗</div>
        <h1>Invalid Link</h1>
        <p>{error}</p>
        <p className="help-text">If you believe this is a mistake, contact support@successlane.io</p>
      </div>
    );
  }

  return (
    <div className="intake-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }

        :root {
          /* Brand palette (Poppins + purple, same tokens as the Analyze /
             loading / landing screens) — variable NAMES kept as --blue* to
             avoid touching every rule below, only the VALUES moved to brand
             purple so this stays a low-risk, easy-to-follow diff. */
          --blue: #7c3aed;
          --blue-light: #f6f4fd;
          --blue-mid: #c4b5fd;
          --success: #059669;
          --success-bg: #d1fae5;
          --slate-50: #f8f7fc;
          --slate-100: #efedf6;
          --slate-200: #e5e1f2;
          --slate-300: #ddd6fe;
          --slate-400: #9691a8;
          --slate-500: #6b6580;
          --slate-600: #4b4560;
          --slate-700: #3f3a52;
          --slate-800: #2a2440;
          --slate-900: #1a1130;
          --danger: #dc2626;
          --radius: 8px;
        }

        /* App.css sets font-family via a bare "*" selector, which directly
           matches every element (including this page's own text) and wins
           over an inherited font-family regardless of ancestor specificity —
           a direct match always beats inheritance. Same fix as the Analyze /
           Checkout screens. */
        .intake-page, .intake-page * { font-family: 'Poppins', -apple-system, BlinkMacSystemFont, sans-serif; }

        .intake-page {
          min-height: 100vh;
          background: var(--slate-50);
          padding: 24px 16px 60px;
        }

        .intake-loading, .intake-error {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 20px;
        }
        .spinner {
          width: 32px;
          height: 32px;
          border: 3px solid var(--slate-200);
          border-top-color: var(--blue);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin-bottom: 16px;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        .error-icon { font-size: 48px; margin-bottom: 16px; }
        .intake-error h1 { font-size: 24px; color: var(--slate-900); margin: 0 0 8px; }
        .intake-error p { color: var(--slate-500); margin: 0 0 8px; }
        .help-text { font-size: 13px; }

        .container {
          max-width: 640px;
          margin: 0 auto;
        }

        .step-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0;
          margin-bottom: 28px;
        }
        .step-dot {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          font-weight: 600;
        }
        .step-dot.active {
          background: var(--blue);
          color: white;
        }
        .step-dot.done {
          background: var(--success);
          color: white;
        }
        .step-dot.inactive {
          background: var(--slate-200);
          color: var(--slate-500);
        }
        .step-line {
          width: 60px;
          height: 2px;
          background: var(--slate-200);
        }
        .step-line.done {
          background: var(--success);
        }

        .card {
          background: white;
          border: 1px solid var(--slate-200);
          border-radius: 16px;
          padding: 28px;
        }

        .form-title {
          font-size: 22px;
          font-weight: 600;
          color: var(--slate-900);
          margin-bottom: 6px;
        }
        .form-sub {
          font-size: 14px;
          color: var(--slate-500);
          margin-bottom: 28px;
          line-height: 1.5;
        }
        .required-star { color: var(--danger); }

        .section-label {
          font-size: 13px;
          font-weight: 600;
          color: var(--blue);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 16px;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--slate-100);
        }

        .field {
          margin-bottom: 18px;
        }
        .field label {
          display: block;
          font-size: 13px;
          font-weight: 500;
          color: var(--slate-700);
          margin-bottom: 4px;
        }
        .field-hint {
          font-size: 11px;
          font-weight: 700;
          color: var(--slate-500);
          margin-bottom: 6px;
        }
        .field-error {
          font-size: 12px;
          color: var(--danger);
          margin-top: 4px;
          display: none;
        }
        .field-error.show {
          display: block;
        }

        /* Field outlines are purple by default (not just on focus) — a
           visible resting-state purple border, matching the brand accent,
           with a deeper purple + glow on focus. */
        input[type="text"],
        input[type="url"],
        select,
        textarea {
          width: 100%;
          padding: 10px 14px;
          border: 1.5px solid var(--blue-mid);
          border-radius: var(--radius);
          font-size: 14px;
          color: var(--slate-900);
          background: white;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        input:focus, select:focus, textarea:focus {
          outline: none;
          border-color: var(--blue);
          box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.15);
        }
        input.error, select.error, textarea.error {
          border-color: var(--danger);
        }
        textarea {
          resize: vertical;
          font-family: inherit;
        }
        select {
          cursor: pointer;
        }

        .company-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 10px;
        }

        .or-divider {
          font-size: 11px;
          color: var(--slate-400);
          text-align: center;
          margin: 12px 0;
        }

        .upload-zone {
          border: 2px dashed var(--blue-mid);
          border-radius: var(--radius);
          padding: 20px;
          text-align: center;
          cursor: pointer;
          transition: border-color 0.15s, background 0.15s;
          position: relative;
        }
        .upload-zone:hover {
          border-color: var(--blue);
          background: var(--blue-light);
        }
        .upload-zone.has-file {
          border-color: var(--success);
          background: var(--success-bg);
        }
        .upload-zone input[type="file"] {
          position: absolute;
          inset: 0;
          opacity: 0;
          cursor: pointer;
        }
        .upload-icon {
          font-size: 28px;
          margin-bottom: 6px;
        }
        .upload-text {
          font-size: 13px;
          color: var(--slate-600);
        }
        .upload-text span {
          font-size: 11px;
          color: var(--slate-400);
        }

        .btn-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px solid var(--slate-100);
        }
        .step-label {
          font-size: 12px;
          color: var(--slate-400);
        }

        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 12px 24px;
          border-radius: var(--radius);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
          border: none;
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .btn-primary {
          background: var(--blue);
          color: white;
        }
        .btn-primary:hover:not(:disabled) {
          background: #6d28d9;
        }
        .btn-success {
          background: var(--success);
          color: white;
        }
        .btn-success:hover:not(:disabled) {
          background: #047857;
        }
        .btn-outline {
          background: white;
          border: 1px solid var(--slate-300);
          color: var(--slate-600);
        }
        .btn-outline:hover {
          background: var(--slate-50);
        }

        /* Review Step */
        .review-card {
          margin-bottom: 20px;
        }
        .review-section {
          padding: 16px;
          background: var(--slate-50);
          border-radius: var(--radius);
          margin-bottom: 12px;
        }
        .review-label {
          font-size: 10px;
          font-weight: 600;
          color: var(--slate-500);
          text-transform: uppercase;
          letter-spacing: 0.3px;
          margin-bottom: 6px;
        }
        .review-value {
          font-size: 14px;
          color: var(--slate-900);
          font-weight: 500;
        }
        .review-sub {
          font-size: 12px;
          color: var(--slate-500);
          margin-top: 2px;
        }
        .review-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 6px;
        }
        .review-tag {
          padding: 4px 10px;
          background: var(--blue-light);
          color: var(--blue);
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
        }

        .info-box {
          background: var(--blue-light);
          border: 1px solid var(--blue-mid);
          border-radius: var(--radius);
          padding: 12px 16px;
          font-size: 12px;
          color: var(--blue);
          line-height: 1.5;
          margin-bottom: 20px;
        }

        /* --- Mobile --------------------------------------------------- */
        /* Desktop layout above is untouched; these only kick in narrower
           than 640px so the web-browser view is unaffected. */
        @media (max-width: 640px) {
          .intake-page { padding: 16px 12px 48px; }
          .card { padding: 18px; border-radius: 14px; }
          .form-title { font-size: 19px; }
          .form-sub { margin-bottom: 20px; }
          .company-grid { grid-template-columns: 1fr; }
          .step-line { width: 36px; }
          .btn-row { flex-direction: column-reverse; align-items: stretch; gap: 10px; }
          .btn-row .step-label { text-align: center; }
          .btn { width: 100%; padding: 13px 20px; }
          .upload-zone { padding: 16px; }
        }
      `}</style>

      <div className="container">
        {/* Brand logo — top of page, matches every other screen in the funnel */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px', cursor: 'pointer' }} onClick={() => navigate('/')}>
          <Logo />
        </div>

        {/* Order ID bar */}
        {(searchParams.get('order') || orderInfo?.order_id) && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '5px 12px', borderRadius: '8px',
              backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0'
            }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5">
                <rect x="3" y="3" width="18" height="18" rx="3"/><path d="M9 9h6M9 12h6M9 15h4"/>
              </svg>
              <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Order</span>
              <span style={{ fontSize: '13px', color: '#0f172a', fontWeight: 700, fontFamily: 'monospace', letterSpacing: '0.03em' }}>
                {orderInfo?.order_id || searchParams.get('order')}
              </span>
            </div>
          </div>
        )}

        {/* Step Indicator */}
        <div className="step-indicator">
          <div className={`step-dot ${step >= 1 ? (step > 1 ? 'done' : 'active') : 'inactive'}`}>
            {step > 1 ? '✓' : '1'}
          </div>
          <div className={`step-line ${step > 1 ? 'done' : ''}`}></div>
          <div className={`step-dot ${step >= 2 ? (step > 2 ? 'done' : 'active') : 'inactive'}`}>
            {step > 2 ? '✓' : '2'}
          </div>
          <div className={`step-line ${step > 2 ? 'done' : ''}`}></div>
          <div className={`step-dot ${step >= 3 ? 'done' : 'inactive'}`}>✓</div>
        </div>

        {/* Step 1: Form */}
        {step === 1 && (
          <div className="card">
            <div className="form-title">Tell us about your career goals</div>
            <div className="form-sub">
              This takes about 10–15 minutes. The more detail you give us, the better your rewrite will be. 
              All fields marked <span className="required-star">*</span> are required.
            </div>

            {/* Target Role */}
            <div className="section-label">Your target role</div>

            <div className="field">
              <label>Target job title <span className="required-star">*</span></label>
              <div className="field-hint">The exact title you're aiming for — e.g. Senior Product Manager, VP of Engineering</div>
              <input
                type="text"
                value={formData.target_title}
                onChange={(e) => setFormData({...formData, target_title: e.target.value})}
                placeholder="Senior Product Manager"
                className={errors.target_title ? 'error' : ''}
              />
              {errors.target_title && <div className="field-error show">{errors.target_title}</div>}
            </div>

            <div className="field">
              <label>Seniority level <span className="required-star">*</span></label>
              <select
                value={formData.seniority}
                onChange={(e) => setFormData({...formData, seniority: e.target.value})}
                className={errors.seniority ? 'error' : ''}
              >
                <option value="">Select your level</option>
                {seniorityLevels.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
              {errors.seniority && <div className="field-error show">{errors.seniority}</div>}
            </div>

            {/* Dream Companies */}
            <div className="section-label" style={{marginTop: '28px'}}>Target Companies or Industry</div>
            <div className="field">
              <label>Up to 5 companies or Industry you would love to work at</label>
              <div className="field-hint">We use this to match the tone and keywords these companies hire for</div>
              <div className="company-grid">
                {[0, 1, 2, 3].map(idx => (
                  <input
                    key={idx}
                    type="text"
                    value={formData.dream_companies[idx]}
                    onChange={(e) => updateCompany(idx, e.target.value)}
                    placeholder={`Company ${idx + 1}`}
                  />
                ))}
              </div>
              <input
                type="text"
                value={formData.dream_companies[4]}
                onChange={(e) => updateCompany(4, e.target.value)}
                placeholder="Company 5 (optional)"
              />
            </div>

            {/* JD — Section 11: Multi-JD support (up to 3) */}
            <div className="section-label" style={{marginTop: '28px'}}>
              Target Job Description <span className="required-star">*</span>
            </div>
            <div className="field">
              <label>Paste up to 3 job descriptions for the same target role</label>
              <div className="field-hint">
                Most important field — paste 2–3 JDs for exact keyword matching.
              </div>
              {targetJds.map((jd, idx) => (
                <div key={idx} style={{marginBottom: '12px', padding: '12px', background: '#f8fafc', borderRadius: '10px', border: '1px solid var(--slate-200)'}}>
                  <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px'}}>
                    <span style={{fontSize: '12px', fontWeight: 600, color: 'var(--slate-500)', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                      Job Description {idx + 1}{idx === 0 ? ' (Required)' : ' (Optional)'}
                    </span>
                    {idx > 0 && (
                      <button
                        type="button"
                        onClick={() => setTargetJds(prev => prev.filter((_, i) => i !== idx))}
                        style={{fontSize: '12px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer'}}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <textarea
                    value={jd.text}
                    onChange={(e) => setTargetJds(prev => prev.map((j, i) => i === idx ? { ...j, text: e.target.value } : j))}
                    rows={idx === 0 ? 6 : 4}
                    placeholder={idx === 0 ? 'Paste the job description here…' : 'Paste another JD for the same role (optional)…'}
                    style={{width: '100%', marginBottom: 0}}
                  />
                </div>
              ))}
              {targetJds.length < 3 && (
                <button
                  type="button"
                  onClick={() => setTargetJds(prev => [...prev, { text: '', source_type: 'pasted', source_url: '', matches_role: true }])}
                  style={{fontSize: '13px', color: 'var(--blue)', background: 'transparent', border: '1px dashed var(--blue)', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', width: '100%', marginTop: '4px'}}
                >
                  + Add another JD for the same role
                </button>
              )}
              {errors.job_description && <div className="error-msg">{errors.job_description}</div>}
            </div>

            {/* LinkedIn - Enhanced with Import */}
            <div className="section-label" style={{marginTop: '28px'}}>
              Your current LinkedIn profile <span className="required-star">*</span>
              <span style={{fontWeight: 400, fontSize: '12px', color: 'var(--slate-500)', marginLeft: '8px', textTransform: 'none', letterSpacing: 0}}>
                — source of truth for all role dates &amp; titles
              </span>
            </div>

            {/* Cached profile review — shown instead of import UI when we already have the profile on file */}
            {importedProfile && importedProfile.cached && (
              <div className="field" style={{marginBottom: '16px'}}>
                <div style={{
                  padding: '14px 16px',
                  backgroundColor: 'var(--success-bg)',
                  border: '1px solid #a7f3d0',
                  borderRadius: '10px',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '13px',
                  color: 'var(--success)'
                }}>
                  <span>✓</span>
                  <span>We already have your LinkedIn profile from the evaluation. Review below — no need to re-import.</span>
                </div>
                <div style={{
                  padding: '14px 16px',
                  backgroundColor: 'white',
                  border: '1px solid var(--slate-200)',
                  borderRadius: '10px',
                  marginBottom: '12px'
                }}>
                  <div style={{fontSize: '15px', fontWeight: 600, color: 'var(--slate-900)'}}>{importedProfile.full_name}</div>
                  {importedProfile.headline && (
                    <div style={{fontSize: '13px', color: 'var(--slate-500)', marginTop: '2px'}}>{importedProfile.headline}</div>
                  )}
                </div>
                {(importedProfile.experiences || []).length > 0 && (
                  <ProfessionalTimeline experiences={importedProfile.experiences} />
                )}
                <button
                  type="button"
                  onClick={() => setImportedProfile(null)}
                  style={{
                    marginTop: '10px',
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--blue)',
                    fontSize: '12px',
                    cursor: 'pointer',
                    padding: 0
                  }}
                >
                  Use a different profile
                </button>
              </div>
            )}

            {/* Method Toggle — hidden once a cached profile is shown above */}
            {!(importedProfile && importedProfile.cached) && (
            <div className="field" style={{marginBottom: '16px'}}>
              <div style={{
                display: 'flex',
                gap: '4px',
                padding: '4px',
                backgroundColor: 'var(--blue-light)',
                borderRadius: '8px',
                marginBottom: '12px'
              }}>
                {[
                  { id: 'import', icon: '🔗', label: 'LinkedIn URL', testId: 'method-import-btn' },
                  { id: 'pdf',    icon: '📄', label: 'LinkedIn PDF', testId: 'method-pdf-btn' }
                ].map(({ id, icon, label, testId }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setLinkedinInputMethod(id)}
                    data-testid={testId}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '10px 8px',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: 500,
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      backgroundColor: linkedinInputMethod === id ? 'white' : 'transparent',
                      color: linkedinInputMethod === id ? 'var(--blue)' : 'var(--slate-500)',
                      boxShadow: linkedinInputMethod === id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                    }}
                  >
                    {icon} {label}
                  </button>
                ))}
              </div>

              {/* Import from URL */}
              {linkedinInputMethod === 'import' && (
                <div style={{marginTop: '12px'}}>
                  <LinkedInImport
                    onProfileImported={handleProfileImported}
                    onProfileDataChange={(data) => setImportedProfile(data)}
                    sessionId={orderInfo?.order_id}
                    userEmail={orderInfo?.customer_email}
                  />
                  {importedProfile && (
                    <div style={{
                      marginTop: '12px',
                      padding: '10px 14px',
                      backgroundColor: 'var(--success-bg)',
                      borderRadius: '8px',
                      fontSize: '13px',
                      color: 'var(--success)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      ✓ Profile imported: <strong>{importedProfile.full_name}</strong>
                    </div>
                  )}
                </div>
              )}

              {/* LinkedIn PDF Export */}
              {linkedinInputMethod === 'pdf' && (
                <div className={`upload-zone ${uploadedFile ? 'has-file' : ''}`} onClick={() => fileInputRef.current?.click()}>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".pdf"
                    onChange={handleFileUpload}
                  />
                  <div className="upload-icon">{uploadedFile ? '✓' : '📄'}</div>
                  <div className="upload-text">
                    {uploadedFile ? (
                      <>{uploadedFile.name}</>
                    ) : (
                      <>Click to upload your LinkedIn PDF<br/><span>Export from LinkedIn → Me → View Profile → More → Save to PDF</span></>
                    )}
                  </div>
                </div>
              )}

              {errors.linkedin && <div className="field-error show">{errors.linkedin}</div>}
            </div>
            )}

            {/* CV / Resume Upload */}
            <div className="section-label" style={{marginTop: '28px'}}>Upload your CV / Resume <span className="required-star">*</span></div>
            <div className="field">
              <div
                className={`upload-zone ${cvFile ? 'has-file' : ''}`}
                onClick={() => cvFileInputRef.current?.click()}
              >
                <input
                  type="file"
                  ref={cvFileInputRef}
                  accept=".pdf,.docx"
                  onChange={handleCvUpload}
                />
                <div className="upload-icon">{cvFile ? '✓' : '📎'}</div>
                <div className="upload-text">
                  {cvFile ? (
                    <>
                      {cvFile.name}
                      <span style={{marginTop: '4px', display: 'block'}}>Click to replace</span>
                    </>
                  ) : (
                    <>
                      Click to upload your CV or resume
                      <span>Accepted formats: PDF or Word (.docx)</span>
                    </>
                  )}
                </div>
              </div>
              {errors.cv && <div className="field-error show">{errors.cv}</div>}
            </div>

            {/* Achievements */}
            <div id="achievements-section" className="section-label" style={{marginTop: '28px'}}>Achievements</div>
            <div className="field-hint" style={{marginBottom: '16px'}}>
              Mention any achievements which is not there in the CV
            </div>
            <div className="field">
              <label>Achievements <span className="required-star">*</span></label>
              <textarea
                value={formData.achievement_1}
                onChange={(e) => setFormData({...formData, achievement_1: e.target.value})}
                rows={4}
                placeholder="e.g. Grew checkout conversion by 34%, generating ₹2Cr+ in incremental revenue at TechCorp"
                className={errors.achievement_1 ? 'error' : ''}
              />
              {errors.achievement_1 && <div className="field-error show">{errors.achievement_1}</div>}
            </div>

            {/* Anything else */}
            <div className="section-label" style={{marginTop: '28px'}}>Anything else</div>
            <div className="field">
              <label>What else should we know or highlight?</label>
              <div className="field-hint">Awards, certifications, side projects, specific experiences you're proud of, career pivots, anything</div>
              <textarea
                value={formData.extra_notes}
                onChange={(e) => setFormData({...formData, extra_notes: e.target.value})}
                rows={3}
                placeholder="e.g. I was promoted twice in 3 years. I also have a PMP certification. I'm open to remote roles."
              />
            </div>

            <div className="btn-row">
              <span className="step-label">Step 1 of 2</span>
              <button type="button" className="btn btn-primary" onClick={handleNext}>
                Review before submitting →
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Review */}
        {step === 2 && (
          <div className="card">
            <div className="form-title">Review your answers</div>
            <div className="form-sub">Make sure everything looks right before we start working on your profile. Click any section to edit.</div>

            <div className="review-card">
              <div className="review-section" onClick={() => setStep(1)} style={{cursor: 'pointer'}}>
                <div className="review-label">Target Role</div>
                <div className="review-value">{formData.target_title}</div>
                <div className="review-sub">{formData.seniority}</div>
              </div>

              {formData.dream_companies.some(c => c.trim()) && (
                <div className="review-section">
                  <div className="review-label">Dream Companies</div>
                  <div className="review-tags">
                    {formData.dream_companies.filter(c => c.trim()).map((company, idx) => (
                      <span key={idx} className="review-tag">{company}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="review-section">
                <div className="review-label">LinkedIn Profile</div>
                <div className="review-value">
                  {importedProfile ? (
                    <span style={{color: 'var(--success)'}}>✓ Imported: {importedProfile.full_name} ({importedProfile.current_position} @ {importedProfile.current_company})</span>
                  ) : uploadedFile ? (
                    `LinkedIn PDF: ${uploadedFile.name}`
                  ) : (
                    'Not provided'
                  )}
                </div>
              </div>

              {cvFile && (
                <div className="review-section">
                  <div className="review-label">CV / Resume</div>
                  <div className="review-value">{cvFile.name}</div>
                </div>
              )}

              <div className="review-section">
                <div className="review-label">Achievements</div>
                <div className="review-value">{formData.achievement_1}</div>
              </div>
            </div>

            <div className="info-box">
              Once you submit, our expert will begin working on your profile. You'll receive a WhatsApp message confirming we've started.
            </div>

            {submitError && (
              <div style={{
                background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C',
                borderRadius: 10, padding: '12px 14px', fontSize: 14, marginTop: 14,
              }}>
                ⚠ {submitError}
              </div>
            )}

            <div className="btn-row">
              <button type="button" className="btn btn-outline" onClick={() => setStep(1)}>
                ← Edit answers
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit and start my rewrite →'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IntakeFormPage;
